import { ChevronLeftIconRounded } from '@shared/assets/icons';
import { IconButton } from '@shared/ui/icon-button';
import type { DatePickerInputStatus } from '@shared/ui/kit/date-picker/model/types';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSendMessagesFlow } from './lib/use-send-messages-flow';
import { SendProgressLoader } from './ui/send-progress-loader';
import { SendStatisticsModal } from './ui/send-statistics-modal';
import { calcSmsParts20_70 } from '@shared/libs/sms-utils';
import styles from './event-send-invitations-page.module.scss';
import { getTextFromHtml } from '@features/modal-invitation/lib/getTextFromHtml';
import { InvitationSettingsSection } from './ui/invitation-settings-section';
import { OutlookMeetingSettingsSection } from '@features/outlook-meeting';
import { TicketSettingsSection } from './ui/ticket-settings-section';
import { PreviewSummarySection } from './ui/preview-summary-section';
import type { EventSettings } from '@features/api/use-get-settings';
import type { SendInvitationMessagesInput } from './api/useSendInvitationMessages';
import { INVITATION_DELIVERY_TIME, TICKET_DELIVERY_TIME, invitationSendPayload } from './lib/invitation-send-payload';
import { usePreviewChannels } from './lib/use-preview-channels';
import { INVITATION_TYPE_EMAIL, INVITATION_TYPE_SMS, INVITATION_TYPE_VK_TEAMS } from './lib/invitation-types';
import { mapDeliveryMethodsToSelectedChannels } from './lib/invitation-channel-options';
import { normalizeDateOnly } from '@shared/libs/date-validation';
import {
  INVALID_DATE_WARNING,
  PRIMARY_ALERT_INVITATION_VS_EVENT_END,
  PRIMARY_ALERT_INVITATION_VS_TICKET_DATE,
  PRIMARY_INVITATION_BODY_REQUIRED,
  PRIMARY_REQUIRED_FIELD,
  evaluatePrimaryInvitationDateIssue,
} from './lib/primary-invitation-date';
import type { OutlookTimeSlotOption } from '@features/outlook-meeting';
import { findTimeSlotFromDate } from '@features/outlook-meeting';
import { evaluateRepeatSendFlow, INVITATION_RESEND_BLOCKED_MESSAGE } from './lib/repeat-send-flow';
import type { SelectValue } from '@shared/ui/select';
import { Alert, ALERT_VARIANTS } from '@features/modal-invitation/ui/alert';
import { getInitialInviteFormState, useInviteFormPersistence } from './lib/use-invite-form-persistence';
import { EventStartedSend } from '@entities/event';

const TICKET_INVALID_DATE_INLINE = 'Неверно указана дата';
const TICKET_ALERT_BEFORE_INVITATION = 'Дата отправки билета раньше срока действия приглашения';
const TICKET_ALERT_AFTER_EVENT_END = 'Дата отправки билета позже даты проведения мероприятия';
const TICKET_ALERT_BEFORE_TODAY = 'Дата отправки билета раньше текущей даты';
const TICKET_DATE_REQUIRED = 'Обязательное поле';
const TICKET_TYPE_REQUIRED = 'Обязательное поле';
const TICKET_TEXT_REQUIRED = 'Введите текст билета';
const PREVIEW_DATE_TIME_NOT_SPECIFIED = 'Дата и время не указаны';
const SHORT_WEEKDAYS = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];

const cleanSingleLineText = (value: string) => value.replace(/\r?\n/g, '');

export interface EventSendInvitationsScreenProps {
  onBack: () => void;
  eventId: number;
  eventName: string | null;
  eventDateStart: string | null;
  eventDateEnd: string | null;
  eventSettings: EventSettings | null;
  ticketIds: number[];
  emailWillBeSent: number;
  smsWillBeSent: number;
  vkWillBeSent: number;
  totalInvites: number;
  sendMessages: (input: SendInvitationMessagesInput) => Promise<number | undefined>;
  onSent?: () => void;
}

const parseDate = (value: string | null) => (value ? new Date(value) : null);

export const EventSendInvitationsScreen: FC<EventSendInvitationsScreenProps> = ({
  onBack,
  eventId,
  eventName,
  eventDateStart,
  eventDateEnd,
  eventSettings,
  ticketIds,
  emailWillBeSent,
  smsWillBeSent,
  vkWillBeSent,
  totalInvites,
  sendMessages,
  onSent,
}) => {
  const {
    runSend,
    closeStatistics,
    isWaitingForSend,
    isStartedFlowActive,
    isStatisticsModalVisible,
    isSendFlowActive,
    messagesStats,
  } = useSendMessagesFlow({ eventId, onComplete: onSent });

  const {
    isRepeatSending,
    isInvitationSettingsLocked,
    isTicketSectionLocked,
    isInvitationResendBlocked,
    isPrimaryLikeInvitationFlow,
  } = useMemo(
    () =>
      evaluateRepeatSendFlow({
        totalInvites,
        invitationExpiresAt: eventSettings?.invitationExpiresAt,
        sendTicketAfterAcceptAt: eventSettings?.sendTicketAfterAcceptAt,
        inviteSentAt: eventSettings?.inviteSentAt,
      }),
    [
      eventSettings?.invitationExpiresAt,
      eventSettings?.inviteSentAt,
      eventSettings?.sendTicketAfterAcceptAt,
      totalInvites,
    ]
  );
  const isPrimarySend = !isRepeatSending || isPrimaryLikeInvitationFlow;
  const initialSavedForm = useRef(getInitialInviteFormState(eventId));
  const isClosingRef = useRef(false);
  const [invitationExpiresAt, setInvitationExpiresAt] = useState<Date | null>(
    () => initialSavedForm.current.invitationExpiresAt ?? null
  );
  const [selectedInvitationChannels, setSelectedInvitationChannels] = useState<SelectValue[]>(
    () => initialSavedForm.current.selectedInvitationChannels ?? []
  );
  const [invitationText, setInvitationText] = useState(() => initialSavedForm.current.invitationText ?? '');
  const [invitationSubject, setInvitationSubject] = useState(() => initialSavedForm.current.invitationSubject ?? '');
  const [inviteAcceptionInfo, setInviteAcceptionInfo] = useState(
    () => initialSavedForm.current.inviteAcceptionInfo ?? ''
  );
  const [isOutlookMeetingEnabled, setIsOutlookMeetingEnabled] = useState(
    () => initialSavedForm.current.isOutlookMeetingEnabled ?? false
  );
  const [outlookMeetingStartTime, setOutlookMeetingStartTime] = useState<OutlookTimeSlotOption | null>(
    () => initialSavedForm.current.outlookMeetingStartTime ?? null
  );
  const [outlookMeetingEndTime, setOutlookMeetingEndTime] = useState<OutlookTimeSlotOption | null>(
    () => initialSavedForm.current.outlookMeetingEndTime ?? null
  );
  const [outlookMeetingLocationOrLink, setOutlookMeetingLocationOrLink] = useState(
    () => initialSavedForm.current.outlookMeetingLocationOrLink ?? ''
  );
  const [outlookMeetingComment, setOutlookMeetingComment] = useState(
    () => initialSavedForm.current.outlookMeetingComment ?? ''
  );
  const [ticketDate, setTicketDate] = useState<Date | null>(() => initialSavedForm.current.ticketDate ?? null);
  const [isSkipTicketSendChecked, setIsSkipTicketSendChecked] = useState(
    () => initialSavedForm.current.isSkipTicketSendChecked ?? false
  );
  const [isTicketSettingsCollapsed, setIsTicketSettingsCollapsed] = useState(
    () => initialSavedForm.current.isTicketSettingsCollapsed ?? false
  );
  const [selectedTicketChannels, setSelectedTicketChannels] = useState<SelectValue[]>(
    () => initialSavedForm.current.selectedTicketChannels ?? []
  );
  const [ticketText, setTicketText] = useState(() => initialSavedForm.current.ticketText ?? '');
  const [ticketSubject, setTicketSubject] = useState(() => initialSavedForm.current.ticketSubject ?? '');
  const [invitationWarnings, setInvitationWarnings] = useState<string[]>([]);
  const [ticketBottomAlerts, setTicketBottomAlerts] = useState<string[]>([]);
  const [ticketDateInlineMessage, setTicketDateInlineMessage] = useState<string | undefined>(undefined);
  const [ticketTypeError, setTicketTypeError] = useState<string | null>(null);
  const [ticketTextError, setTicketTextError] = useState<string | null>(null);

  const [primaryInvitationDateInlineMessage, setPrimaryInvitationDateInlineMessage] = useState<string | null>(null);
  const [primaryInvitationBottomAlertMessage, setPrimaryInvitationBottomAlertMessage] = useState<string | null>(null);
  const [primaryInvitationTypeError, setPrimaryInvitationTypeError] = useState<string | null>(null);
  const [primaryInvitationTextError, setPrimaryInvitationTextError] = useState<string | null>(null);

  const invitationTextPlain = useMemo(() => getTextFromHtml(invitationText), [invitationText]);
  const ticketTextPlain = useMemo(() => getTextFromHtml(ticketText), [ticketText]);
  const isSmsInvitationMode = !selectedInvitationChannels.includes(INVITATION_TYPE_EMAIL);
  const isEmailInvitationMode = selectedInvitationChannels.includes(INVITATION_TYPE_EMAIL);
  const isBothInvitationMode =
    selectedInvitationChannels.includes(INVITATION_TYPE_EMAIL) &&
    selectedInvitationChannels.includes(INVITATION_TYPE_SMS);
  const isSmsTicketMode =
    selectedTicketChannels.length === 0 || !selectedTicketChannels.includes(INVITATION_TYPE_EMAIL);
  const isEmailTicketMode = selectedTicketChannels.includes(INVITATION_TYPE_EMAIL);
  const isBothTicketMode =
    selectedTicketChannels.includes(INVITATION_TYPE_EMAIL) &&
    (selectedTicketChannels.includes(INVITATION_TYPE_SMS) || selectedTicketChannels.includes(INVITATION_TYPE_VK_TEAMS));
  const invitationSmsText = isSmsInvitationMode ? invitationText : invitationTextPlain;
  const ticketSmsText = isSmsTicketMode ? ticketText : ticketTextPlain;
  const invitationTextParts = useMemo(() => calcSmsParts20_70(invitationSmsText), [invitationSmsText]);
  const ticketTextParts = useMemo(() => calcSmsParts20_70(ticketSmsText), [ticketSmsText]);
  const eventDateStartValue = useMemo(() => (eventDateStart ? new Date(eventDateStart) : null), [eventDateStart]);

  const isOutlookSwitcherDisabled = totalInvites > 0;

  const trimmedInvitationPayload = useMemo(
    () => (isSmsInvitationMode ? invitationText.trim() : invitationTextPlain.trim()),
    [isSmsInvitationMode, invitationText, invitationTextPlain]
  );

  const { hasRestoredFromSessionRef } = useInviteFormPersistence({
    eventId,
    isClosingRef,
    invitationExpiresAt,
    selectedInvitationChannels,
    invitationText,
    invitationSubject,
    inviteAcceptionInfo,
    isOutlookMeetingEnabled,
    outlookMeetingStartTime,
    outlookMeetingEndTime,
    outlookMeetingLocationOrLink,
    outlookMeetingComment,
    ticketDate,
    isSkipTicketSendChecked,
    isTicketSettingsCollapsed,
    selectedTicketChannels,
    ticketText,
    ticketSubject,
  });

  const handleBack = useCallback(() => {
    if (isSendFlowActive) return;
    isClosingRef.current = true;
    onBack();
  }, [isSendFlowActive, onBack]);

  useEffect(() => {
    if (!eventSettings || hasRestoredFromSessionRef.current || isPrimarySend) return;

    const nextInvitationDate = parseDate(eventSettings.invitationExpiresAt);
    const nextTicketDate = parseDate(eventSettings.sendTicketAfterAcceptAt);
    const nextTicketChannels = mapDeliveryMethodsToSelectedChannels(eventSettings.ticketDeliveryMethod);
    const hasTicketEmailInSettings = nextTicketChannels.includes(INVITATION_TYPE_EMAIL);
    const nextTicketMessage = hasTicketEmailInSettings
      ? eventSettings.emailTicketDeliveryMessage || eventSettings.ticketDeliveryMessage || ''
      : eventSettings.ticketDeliveryMessage || '';

    setInvitationExpiresAt(isPrimarySend ? null : nextInvitationDate);
    setTicketDate(isPrimarySend ? null : nextTicketDate);
    setSelectedTicketChannels(isPrimarySend ? [] : nextTicketChannels);
    setTicketText(isPrimarySend ? '' : nextTicketMessage);
    setInviteAcceptionInfo(isPrimarySend ? '' : eventSettings.inviteAcceptionInfo ?? '');
    setInvitationSubject(isPrimarySend ? '' : eventSettings.invitationEmailSubject ?? '');
    const isTicketSendingDisabled = eventSettings.skipTicketsOnAccept === true;
    const isImmediateTicketSend = !isTicketSendingDisabled && !eventSettings.sendTicketAfterAcceptAt;

    setIsSkipTicketSendChecked(isPrimarySend ? false : isImmediateTicketSend);
    setIsTicketSettingsCollapsed(isPrimarySend ? false : isTicketSendingDisabled);
  }, [eventSettings, isPrimarySend]);

  // Тема билета — настройка мероприятия: показываем сохранённую и при первой отправке,
  // иначе заданная на странице отправки билетов тема потерялась бы.
  useEffect(() => {
    if (!eventSettings || hasRestoredFromSessionRef.current) return;

    setTicketSubject(eventSettings.ticketEmailSubject ?? '');
  }, [eventSettings, hasRestoredFromSessionRef]);

  useEffect(() => {
    if (!eventSettings || totalInvites === 0) return;

    const hasCalendarMeetingData = Boolean(
      eventSettings.calendarMeetingDateStart ||
        eventSettings.calendarMeetingDateEnd ||
        eventSettings.calendarMeetingPoint ||
        eventSettings.calendarMeetingDescription
    );

    if (!hasCalendarMeetingData) {
      setIsOutlookMeetingEnabled(false);
      setOutlookMeetingStartTime(null);
      setOutlookMeetingEndTime(null);
      setOutlookMeetingLocationOrLink('');
      setOutlookMeetingComment('');
      return;
    }

    setIsOutlookMeetingEnabled(true);
    setOutlookMeetingStartTime(findTimeSlotFromDate(eventSettings.calendarMeetingDateStart));
    setOutlookMeetingEndTime(findTimeSlotFromDate(eventSettings.calendarMeetingDateEnd));
    setOutlookMeetingLocationOrLink(eventSettings.calendarMeetingPoint ?? '');
    setOutlookMeetingComment(eventSettings.calendarMeetingDescription ?? '');
  }, [eventSettings, totalInvites]);

  useEffect(() => {
    if (isPrimarySend || isPrimaryLikeInvitationFlow) {
      setInvitationWarnings([]);
      return;
    }

    const warnings: string[] = [];
    const normalizedInvitationDate = invitationExpiresAt ? normalizeDateOnly(invitationExpiresAt) : null;
    const normalizedTicketDate = ticketDate ? normalizeDateOnly(ticketDate) : null;
    const normalizedEventEndDate = eventDateEnd ? normalizeDateOnly(new Date(eventDateEnd)) : null;

    if (normalizedInvitationDate && normalizedEventEndDate && normalizedInvitationDate > normalizedEventEndDate) {
      warnings.push(INVALID_DATE_WARNING);
    }

    if (normalizedInvitationDate && normalizedTicketDate && normalizedInvitationDate > normalizedTicketDate) {
      warnings.push(PRIMARY_ALERT_INVITATION_VS_TICKET_DATE);
    }

    setInvitationWarnings(warnings);
  }, [eventDateEnd, invitationExpiresAt, isPrimaryLikeInvitationFlow, isPrimarySend, ticketDate]);

  const evaluateTicketDateWarnings = useCallback(() => {
    if (isTicketSettingsCollapsed || !ticketDate) {
      return [];
    }

    const warnings: string[] = [];
    const normalizedTicketDate = normalizeDateOnly(ticketDate);
    const normalizedToday = normalizeDateOnly(new Date());
    const normalizedInvitationDate = invitationExpiresAt ? normalizeDateOnly(invitationExpiresAt) : null;
    const normalizedEventEndDate = eventDateEnd ? normalizeDateOnly(new Date(eventDateEnd)) : null;

    if (normalizedInvitationDate && normalizedTicketDate < normalizedInvitationDate) {
      warnings.push(TICKET_ALERT_BEFORE_INVITATION);
    }

    if (normalizedEventEndDate && normalizedTicketDate > normalizedEventEndDate) {
      warnings.push(TICKET_ALERT_AFTER_EVENT_END);
    }

    if (normalizedTicketDate < normalizedToday) {
      warnings.push(TICKET_ALERT_BEFORE_TODAY);
    }

    return warnings;
  }, [eventDateEnd, invitationExpiresAt, isTicketSettingsCollapsed, ticketDate]);

  useEffect(() => {
    if (isTicketSettingsCollapsed || !ticketDate) {
      setTicketBottomAlerts([]);
      return;
    }
    setTicketBottomAlerts(evaluateTicketDateWarnings());
  }, [evaluateTicketDateWarnings, isTicketSettingsCollapsed, ticketDate]);

  const applyPrimaryInvitationDateValidity = useCallback(() => {
    const result = evaluatePrimaryInvitationDateIssue({
      invitationExpiresAt,
      eventDateEnd,
      ticketDate,
      ticketsBlockActive: !isTicketSettingsCollapsed,
    });
    setPrimaryInvitationDateInlineMessage(result.inline);
    setPrimaryInvitationBottomAlertMessage(result.alert);
    return result;
  }, [eventDateEnd, invitationExpiresAt, isTicketSettingsCollapsed, ticketDate]);

  const validatePrimaryInvitationOnSubmit = useCallback(() => {
    const dateResult = evaluatePrimaryInvitationDateIssue({
      invitationExpiresAt,
      eventDateEnd,
      ticketDate,
      ticketsBlockActive: !isTicketSettingsCollapsed,
    });

    const typeIssue = selectedInvitationChannels.length ? null : PRIMARY_REQUIRED_FIELD;
    const textIssue: string | null = trimmedInvitationPayload ? null : PRIMARY_INVITATION_BODY_REQUIRED;

    setPrimaryInvitationDateInlineMessage(dateResult.inline);
    setPrimaryInvitationBottomAlertMessage(dateResult.alert);
    setPrimaryInvitationTypeError(typeIssue);
    setPrimaryInvitationTextError(textIssue);

    return !(dateResult.invalid || typeIssue || textIssue);
  }, [
    trimmedInvitationPayload,
    eventDateEnd,
    invitationExpiresAt,
    selectedInvitationChannels,
    isTicketSettingsCollapsed,
    ticketDate,
  ]);

  const validateRepeatInvitationOnSubmit = useCallback(() => {
    const typeIssue = selectedInvitationChannels.length ? null : PRIMARY_REQUIRED_FIELD;
    const textIssue: string | null = trimmedInvitationPayload ? null : PRIMARY_INVITATION_BODY_REQUIRED;

    setPrimaryInvitationTypeError(typeIssue);
    setPrimaryInvitationTextError(textIssue);

    return !(typeIssue || textIssue);
  }, [selectedInvitationChannels, trimmedInvitationPayload]);

  const validateTicketSectionOnSubmit = useCallback(() => {
    if (isTicketSettingsCollapsed || isTicketSectionLocked) {
      setTicketDateInlineMessage(undefined);
      setTicketBottomAlerts([]);
      setTicketTypeError(null);
      setTicketTextError(null);
      return true;
    }

    const typeIssue: string | null = selectedTicketChannels.length ? null : TICKET_TYPE_REQUIRED;
    const hasTicketText = !!getTextFromHtml(ticketText).trim();
    const textIssue: string | null = hasTicketText ? null : TICKET_TEXT_REQUIRED;
    setTicketTypeError(typeIssue);
    setTicketTextError(textIssue);

    if (isSkipTicketSendChecked) {
      setTicketDateInlineMessage(undefined);
      setTicketBottomAlerts([]);
      return !(typeIssue || textIssue);
    }

    if (!ticketDate) {
      setTicketDateInlineMessage(TICKET_DATE_REQUIRED);
      setTicketBottomAlerts([]);
      return !(typeIssue || textIssue);
    }

    const warnings = evaluateTicketDateWarnings();
    setTicketBottomAlerts(warnings);
    setTicketDateInlineMessage(warnings.length > 0 ? TICKET_INVALID_DATE_INLINE : undefined);
    return !(typeIssue || textIssue || warnings.length > 0);
  }, [
    evaluateTicketDateWarnings,
    isSkipTicketSendChecked,
    isTicketSectionLocked,
    isTicketSettingsCollapsed,
    ticketDate,
    ticketText,
    selectedTicketChannels,
  ]);

  const handleInvitationCalendarVisibilityChange = useCallback(
    (open: boolean) => {
      if (open || isInvitationSettingsLocked) return;
      applyPrimaryInvitationDateValidity();
    },
    [applyPrimaryInvitationDateValidity, isInvitationSettingsLocked]
  );

  const handleInvitationDateChangeWithReset = useCallback(
    (dates: [Date | null, Date | null]) => {
      setInvitationExpiresAt(dates[0] ?? null);
      if (!isInvitationSettingsLocked) {
        setPrimaryInvitationDateInlineMessage(null);
        setPrimaryInvitationBottomAlertMessage(null);
      }
    },
    [isInvitationSettingsLocked]
  );

  const handleInvitationChannelsChangeInner = useCallback((values: SelectValue | SelectValue[]) => {
    const channels = Array.isArray(values) ? values : [];

    setSelectedInvitationChannels(channels);
    setPrimaryInvitationTypeError(null);

    if (!channels.includes(INVITATION_TYPE_EMAIL)) {
      setInvitationText((prevText) => cleanSingleLineText(getTextFromHtml(prevText)));
    }
  }, []);

  const handleInvitationTextChangeInner = useCallback(
    (value: string) => {
      setPrimaryInvitationTextError(null);

      if (isSmsInvitationMode) {
        setInvitationText(cleanSingleLineText(value));
        return;
      }

      if (!getTextFromHtml(value).trim()) {
        setInvitationText('');
        return;
      }

      setInvitationText(value);
    },
    [isSmsInvitationMode]
  );

  const handleAttemptSendInvitations = useCallback(async () => {
    if (isInvitationResendBlocked) return;

    const valid = isInvitationSettingsLocked ? validateRepeatInvitationOnSubmit() : validatePrimaryInvitationOnSubmit();
    const isTicketValid = validateTicketSectionOnSubmit();
    if (!valid || !isTicketValid || !invitationExpiresAt) return;

    const effectiveInviteAcceptionInfo = isInvitationSettingsLocked
      ? eventSettings?.inviteAcceptionInfo ?? ''
      : inviteAcceptionInfo;

    const payload = invitationSendPayload({
      ticketIds,
      selectedInvitationChannels,
      invitationText,
      invitationSubject,
      invitationExpiresAt,
      inviteAcceptionInfo: effectiveInviteAcceptionInfo,
      isTicketSettingsCollapsed,
      isSkipTicketSendChecked,
      selectedTicketChannels,
      ticketText: isTicketSectionLocked ? '' : ticketText,
      ticketSubject,
      lockedTicketSubject: isTicketSectionLocked ? eventSettings?.ticketEmailSubject : null,
      lockedTicketPlainMessage: isTicketSectionLocked ? eventSettings?.ticketDeliveryMessage : null,
      lockedTicketEmailMessage: isTicketSectionLocked ? eventSettings?.emailTicketDeliveryMessage : null,
      ticketDate,
      isOutlookMeetingEnabled,
      outlookMeetingDate: eventDateStartValue,
      outlookMeetingStartTime,
      outlookMeetingEndTime,
      outlookMeetingLocationOrLink,
      outlookMeetingComment,
    });

    await runSend(() => sendMessages(payload));
  }, [
    inviteAcceptionInfo,
    invitationExpiresAt,
    invitationText,
    invitationSubject,
    ticketSubject,
    selectedInvitationChannels,
    isInvitationResendBlocked,
    isInvitationSettingsLocked,
    isTicketSectionLocked,
    isOutlookMeetingEnabled,
    isSkipTicketSendChecked,
    eventSettings,
    eventDateStartValue,
    outlookMeetingComment,
    outlookMeetingEndTime,
    outlookMeetingLocationOrLink,
    outlookMeetingStartTime,
    sendMessages,
    runSend,
    ticketDate,
    ticketIds,
    ticketText,
    selectedTicketChannels,
    validateTicketSectionOnSubmit,
    validatePrimaryInvitationOnSubmit,
    validateRepeatInvitationOnSubmit,
  ]);

  const repeatingBottomInviteAlert =
    !isPrimarySend && invitationWarnings.includes(INVALID_DATE_WARNING) ? PRIMARY_ALERT_INVITATION_VS_EVENT_END : null;

  const invitationDatePickerHasErrorHighlight =
    !isInvitationSettingsLocked &&
    (primaryInvitationDateInlineMessage !== null || primaryInvitationBottomAlertMessage !== null);
  const invitationDatePickerInputMessage = primaryInvitationDateInlineMessage ?? undefined;
  const invitationDatePickerStatus: DatePickerInputStatus = invitationDatePickerHasErrorHighlight ? 'error' : 'default';

  const invitationBottomAlertText = isInvitationSettingsLocked
    ? repeatingBottomInviteAlert
    : primaryInvitationBottomAlertMessage;
  const ticketDatePickerStatus: DatePickerInputStatus = ticketDateInlineMessage ? 'error' : 'default';
  const ticketDatePickerInputMessage = ticketDateInlineMessage;

  const previewLink = globalThis.window?.location?.href || '';
  const shouldShowTicketsInPreview = !isTicketSettingsCollapsed && selectedTicketChannels.length > 0;
  const deliveryCount = Math.max(emailWillBeSent, smsWillBeSent, vkWillBeSent);
  const eventDateLabel = eventDateStartValue
    ? eventDateStartValue.toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
        weekday: 'long',
      })
    : '';

  const formatPreviewDateTime = (date: Date | null, time: string) => {
    if (!date) return PREVIEW_DATE_TIME_NOT_SPECIFIED;
    const weekday = SHORT_WEEKDAYS[date.getDay()];
    const dayMonth = date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
    return `${weekday}, ${dayMonth} ${time}`;
  };

  const invitationHasDateError = !!primaryInvitationDateInlineMessage || !!primaryInvitationBottomAlertMessage;
  const invitationDateSelected = !!invitationExpiresAt && !invitationHasDateError;
  const invitationDateTime = formatPreviewDateTime(invitationExpiresAt, INVITATION_DELIVERY_TIME);
  const invitationDeliveryLabel = invitationDateSelected ? `до ${invitationDateTime}` : PREVIEW_DATE_TIME_NOT_SPECIFIED;
  const ticketDateSelected = !!ticketDate && !ticketDateInlineMessage && !ticketBottomAlerts.length;
  const ticketDateTime = formatPreviewDateTime(ticketDate, TICKET_DELIVERY_TIME);
  const ticketDeliveryLabel = ticketDateSelected ? `Отправка ${ticketDateTime}` : PREVIEW_DATE_TIME_NOT_SPECIFIED;
  const { invitationChannels, ticketChannels } = usePreviewChannels({
    emailCount: emailWillBeSent,
    smsCount: smsWillBeSent,
    vkCount: vkWillBeSent,
    invitation: {
      selectedChannels: selectedInvitationChannels,
      isOutlookMeetingEnabled,
      textParts: invitationTextParts,
    },
    ticket: {
      selectedChannels: selectedTicketChannels,
      isSettingsCollapsed: isTicketSettingsCollapsed,
      textParts: ticketTextParts,
    },
  });

  const handleTicketChannelsChange = useCallback((values: SelectValue | SelectValue[]) => {
    const channels = Array.isArray(values) ? values : [];
    setSelectedTicketChannels(channels);
    setTicketTypeError(null);
    if (!channels.includes(INVITATION_TYPE_EMAIL)) {
      setTicketText((prevText) => cleanSingleLineText(getTextFromHtml(prevText)));
    }
  }, []);

  const handleSkipTicketSendToggle = useCallback(() => {
    setIsSkipTicketSendChecked((prev) => {
      const next = !prev;
      if (next) {
        setTicketDate(null);
        setTicketDateInlineMessage(undefined);
        setTicketBottomAlerts([]);
      }
      return next;
    });
  }, []);

  const handleOutlookMeetingToggle = useCallback(() => {
    if (isOutlookSwitcherDisabled) return;

    setIsOutlookMeetingEnabled((prev) => {
      const next = !prev;
      if (!next) {
        setOutlookMeetingStartTime(null);
        setOutlookMeetingEndTime(null);
        setOutlookMeetingLocationOrLink('');
        setOutlookMeetingComment('');
      }
      return next;
    });
  }, [isOutlookSwitcherDisabled]);

  const handleOutlookMeetingStartTimeChange = useCallback((option: OutlookTimeSlotOption | null) => {
    setOutlookMeetingStartTime(option);
  }, []);

  const handleOutlookMeetingEndTimeChange = useCallback((option: OutlookTimeSlotOption | null) => {
    setOutlookMeetingEndTime(option);
  }, []);

  const handleOutlookMeetingLocationOrLinkChange = useCallback((value: string) => {
    setOutlookMeetingLocationOrLink(value);
  }, []);

  const handleOutlookMeetingCommentChange = useCallback((value: string) => {
    setOutlookMeetingComment(value);
  }, []);

  const handleInviteAcceptionInfoChange = useCallback((value: string) => {
    setInviteAcceptionInfo(cleanSingleLineText(value));
  }, []);

  const handleToggleTicketSettingsCollapsed = useCallback(() => {
    setIsTicketSettingsCollapsed((prev) => !prev);
  }, []);

  const handleTicketDateChange = useCallback((dates: [Date | null, Date | null]) => {
    setTicketDate(dates[0] || null);
    setTicketDateInlineMessage(undefined);
  }, []);

  const handleTicketTextChange = useCallback((value: string) => {
    setTicketText(cleanSingleLineText(value));
    setTicketTextError(null);
  }, []);

  return (
    <main className={styles.container}>
      {!isStartedFlowActive && (
        <nav className={styles.back}>
          <IconButton
            onClick={handleBack}
            icon={<ChevronLeftIconRounded variant="onDeep" />}
            variant="primary"
            size="small"
          >
            Назад
          </IconButton>
        </nav>
      )}

      {!isSendFlowActive && !isStartedFlowActive && (
        <div className={styles.content}>
          <div className={styles.settingsColumn}>
            {isRepeatSending && (
              <div className={styles.repeatSendWarning}>
                <Alert variant={ALERT_VARIANTS.WARNING}>{INVITATION_RESEND_BLOCKED_MESSAGE}</Alert>
              </div>
            )}
            <section className={styles.settingsPanel}>
              <InvitationSettingsSection
                values={{
                  invitationExpiresAt,
                  selectedInvitationChannels,
                  isSmsInvitationMode,
                  isBothInvitationMode,
                  invitationSmsText,
                  invitationTextParts,
                  invitationText,
                  invitationSubject,
                  isEmailInvitationMode,
                  inviteAcceptionInfo,
                  isOutlookMeetingEnabled,
                  previewLink,
                  warnings: invitationWarnings,
                }}
                validation={{
                  invitationDatePickerStatus,
                  invitationDatePickerInputMessage,
                  invitationDateReadOnly: isInvitationSettingsLocked,
                  inviteAcceptionInfoReadOnly: isInvitationSettingsLocked,
                  invitationTypeDropdownError: primaryInvitationTypeError,
                  invitationTextBodyError: primaryInvitationTextError,
                  invitationBottomAlertText,
                  outlookMeetingReadOnly: isOutlookSwitcherDisabled,
                }}
                actions={{
                  onInvitationDateChange: handleInvitationDateChangeWithReset,
                  onInvitationCalendarVisibilityChange: handleInvitationCalendarVisibilityChange,
                  onInvitationChannelsChange: handleInvitationChannelsChangeInner,
                  onInvitationSubjectChange: setInvitationSubject,
                  onInvitationTextChange: handleInvitationTextChangeInner,
                  onInviteAcceptionInfoChange: handleInviteAcceptionInfoChange,
                  onOutlookMeetingToggle: handleOutlookMeetingToggle,
                }}
              />
              {isOutlookMeetingEnabled && (
                <OutlookMeetingSettingsSection
                  readOnly={isOutlookSwitcherDisabled}
                  values={{
                    eventMeetingDate: eventDateStartValue,
                    outlookMeetingStartTime,
                    outlookMeetingEndTime,
                    outlookMeetingLocationOrLink,
                    outlookMeetingComment,
                  }}
                  actions={{
                    onOutlookMeetingStartTimeChange: handleOutlookMeetingStartTimeChange,
                    onOutlookMeetingEndTimeChange: handleOutlookMeetingEndTimeChange,
                    onOutlookMeetingLocationOrLinkChange: handleOutlookMeetingLocationOrLinkChange,
                    onOutlookMeetingCommentChange: handleOutlookMeetingCommentChange,
                  }}
                />
              )}
              <TicketSettingsSection
                values={{
                  isTicketSettingsCollapsed,
                  isSkipTicketSendChecked,
                  ticketDate,
                  selectedTicketChannels,
                  isSmsTicketMode,
                  isBothTicketMode,
                  isEmailTicketMode,
                  ticketSmsText,
                  ticketText,
                  ticketSubject,
                  ticketTextParts,
                  previewLink,
                }}
                validation={{
                  ticketDatePickerStatus,
                  ticketDatePickerInputMessage,
                  ticketBottomAlerts,
                  showImmediateSendInfoAlert: isPrimarySend && !isTicketSettingsCollapsed && isSkipTicketSendChecked,
                  isRepeatSending,
                  ticketTypeError,
                  ticketTextError,
                }}
                actions={{
                  onToggleSettingsCollapsed: handleToggleTicketSettingsCollapsed,
                  onToggleSkipTicketSend: handleSkipTicketSendToggle,
                  onTicketDateChange: handleTicketDateChange,
                  onTicketChannelsChange: handleTicketChannelsChange,
                  onTicketSubjectChange: setTicketSubject,
                  onTicketTextChange: handleTicketTextChange,
                }}
              />
            </section>
          </div>
          <PreviewSummarySection
            onSendClick={handleAttemptSendInvitations}
            isSendDisabled={isInvitationResendBlocked}
            eventName={eventName}
            eventDateLabel={eventDateLabel}
            invitesCount={deliveryCount}
            ticketsCount={deliveryCount}
            shouldShowTickets={shouldShowTicketsInPreview}
            invitationSummary={{
              channels: invitationChannels,
              deliveryLabel: invitationDeliveryLabel,
              dateSelected: invitationDateSelected,
            }}
            ticketSummary={{
              channels: ticketChannels,
              deliveryLabel: ticketDeliveryLabel,
              dateSelected: ticketDateSelected,
            }}
          />
        </div>
      )}

      {isStartedFlowActive && <EventStartedSend onClose={closeStatistics} />}
      {isWaitingForSend && <SendProgressLoader />}
      <SendStatisticsModal active={isStatisticsModalVisible} messagesStats={messagesStats} onClose={closeStatistics} />
    </main>
  );
};
